import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import colors from '../../utils/colors';
import Icon, { COLORS, ICONS } from './icon';

export const Button = styled.button`
	display: flex;
	padding: 16px 24px;
	width: 100%;
	justify-content: center;
	align-items: center;
	gap: 8px;
	flex: 1 0 0;
	border-radius: 100px;
	background: ${colors.blGrayscale600};

	:hover {
		background: ${colors.blGrayscale500};
	}

	&.open {
		background: ${colors.blGrayscale700};

		label {
			color: ${colors.blGrayscale300};
		}

		svg {
			path {
				fill: ${colors.blGrayscale300};
			}
		}
	}
`;

export const ButtonLabel = styled.label`
	font-size: 13px;
	font-weight: 600;
	line-height: 16px;
	color: ${colors.blGrayscale200};
	cursor: pointer;
	transition: all 250ms;
`;

const OptionsContainer = styled.div`
	position: absolute;
	bottom: calc(100% + 4px);
	left: 0;
	right: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	z-index: 97;
	visibility: hidden;
	opacity: 0;
	transition: all 250ms;
	box-shadow: 0px 11px 130px 0px rgba(26, 26, 31, 0.23),
		0px 2.457px 29.037px 0px rgba(26, 26, 31, 0.14),
		0px 0.732px 8.645px 0px rgba(26, 26, 31, 0.09);

	&.open {
		visibility: visible;
		opacity: 1;
	}
`;

export const OptionButton = styled.button`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	align-self: stretch;
	padding: 8px 16px;
	background: ${colors.blGrayscale800};
	border-radius: 0;

	&:first-of-type {
		border-radius: 12px 12px 0 0;
	}

	&:last-of-type {
		border-radius: 0 0 12px 12px;
	}

	:hover, :active {
		background: ${colors.blGrayscale700};
	}
`;

export const OptionIconContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 16px;
	height: 16px;
	margin-right: 8px;
`;

export const OptionLabel = styled.label`
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	letter-spacing: 0.24px;
	color: ${colors.white};
	cursor: pointer;
`;

interface DropdownButtonProps {
	label: string;
	containerClassName?: string;
	children: React.ReactNode;
}

export default function DropdownButton({
	label,
	containerClassName = '',
	children,
}: DropdownButtonProps) {
	const [optionsOpen, setOptionsOpen] = useState<boolean>(false);

	const handleClick = () => {
		setOptionsOpen(false);
		window.removeEventListener('click', handleClick);
	};

	const handleOpenOptions = () => {
		if (!optionsOpen) {
			setOptionsOpen(true);
			setTimeout(() => {
				window.addEventListener('click', handleClick);
			}, 100);
		}
	};

	useEffect(() => () => {
		window.removeEventListener('click', handleClick);
	}, []);

	return (
		<div style={{ position: 'relative', width: '100%' }}>
			<Button className={classNames(optionsOpen && 'open')} onClick={handleOpenOptions}>
				<ButtonLabel>{label}</ButtonLabel>
				<Icon
					name={
						optionsOpen
							? ICONS.KEYBOARD_ARROW_DOWN
							: ICONS.KEYBOARD_ARROW_UP
					}
					color={COLORS.TEXT_GRAY_200}
					size={12}
				/>
			</Button>
			<OptionsContainer
				className={classNames(containerClassName, optionsOpen ? 'open' : '')}
			>
				{children}
			</OptionsContainer>
		</div>
	);
}